/**
 * OpenTelemetry configuration management
 *
 * Loads configuration from react-native-ultimate-config environment variables.
 * Configuration can be set per-brand via .env.[brand].yml files.
 */

import config from '../../config.generated';

import type {LoggerCallback, TelemetryConfig} from './types';

/**
 * Logger callback registry
 * Allows logger.ts to register itself without creating circular dependency
 */
let loggerCallback: LoggerCallback | null = null;

/**
 * Set the logger callback (called by logger.ts during initialization)
 */
export function setLoggerCallback(callback: LoggerCallback): void {
  loggerCallback = callback;
}

/**
 * Get the logger callback for internal use
 */
export function getLoggerCallback(): LoggerCallback | null {
  return loggerCallback;
}

/**
 * Parse boolean from various value types
 * Handles boolean, string, number, and undefined values
 */
function parseBoolean(value: unknown, defaultValue: boolean): boolean {
  // Handle undefined or null
  if (value === undefined || value === null) {
    return defaultValue;
  }

  // Handle actual boolean values
  if (typeof value === 'boolean') {
    return value;
  }

  // Handle number values (1 = true, 0 = false)
  if (typeof value === 'number') {
    return value !== 0;
  }

  // Handle string values
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') {
      return defaultValue;
    }
    return trimmed.toLowerCase() === 'true' || trimmed === '1';
  }

  // Default case
  return defaultValue;
}

/**
 * Parse float from various value types with validation
 * Handles number, string, and undefined values
 */
function parseFloat(value: unknown, defaultValue: number, min: number, max: number): number {
  // Handle undefined or null
  if (value === undefined || value === null) {
    return defaultValue;
  }

  // Handle actual number values
  if (typeof value === 'number') {
    if (Number.isNaN(value)) {
      if (loggerCallback) {
        loggerCallback('OTEL Config', 'Invalid number value: NaN, using default', {
          timestamp: Date.now(),
          level: 'warn',
          context: {defaultValue},
        });
      }
      return defaultValue;
    }

    if (value < min || value > max) {
      if (loggerCallback) {
        loggerCallback('OTEL Config', 'Value out of range, using default', {
          timestamp: Date.now(),
          level: 'warn',
          context: {value, min, max, defaultValue},
        });
      }
      return defaultValue;
    }

    return value;
  }

  // Handle string values
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') {
      return defaultValue;
    }

    const parsed = Number.parseFloat(trimmed);

    if (Number.isNaN(parsed)) {
      if (loggerCallback) {
        loggerCallback('OTEL Config', 'Invalid number value, using default', {
          timestamp: Date.now(),
          level: 'warn',
          context: {value, defaultValue},
        });
      }
      return defaultValue;
    }

    if (parsed < min || parsed > max) {
      if (loggerCallback) {
        loggerCallback('OTEL Config', 'Value out of range, using default', {
          timestamp: Date.now(),
          level: 'warn',
          context: {parsed, min, max, defaultValue},
        });
      }
      return defaultValue;
    }

    return parsed;
  }

  // Default case
  if (loggerCallback) {
    loggerCallback('OTEL Config', 'Unexpected value type, using default', {
      timestamp: Date.now(),
      level: 'warn',
      context: {valueType: typeof value, defaultValue},
    });
  }
  return defaultValue;
}

/**
 * Get telemetry configuration from environment variables
 *
 * Environment variables (from .env.[brand].yml):
 * - OTEL_ENABLED: true/false (default: false)
 * - OTEL_SERVICE_NAME: string (default: 'frontend-unknown')
 * - OTEL_SAMPLE_RATE: 0.0-1.0 (default: 1.0)
 * - OTEL_DEBUG: true/false (default: false)
 */
export function getTelemetryConfig(): TelemetryConfig {
  const telemetryConfig: TelemetryConfig = {
    enabled: parseBoolean(config.OTEL_ENABLED, false),
    serviceName: config.OTEL_SERVICE_NAME || 'frontend-unknown',
    sampleRate: parseFloat(config.OTEL_SAMPLE_RATE, 1.0, 0.0, 1.0),
    debug: parseBoolean(config.OTEL_DEBUG, false),
  };

  // Validate and warn about configuration issues
  if (telemetryConfig.enabled) {
    if (telemetryConfig.serviceName === 'frontend-unknown') {
      if (loggerCallback) {
        loggerCallback(
          'OTEL Config',
          'OTEL_SERVICE_NAME not configured. Please set it in .env.[brand].yml (e.g., "frontend-bottlemart")',
          {
            timestamp: Date.now(),
            level: 'warn',
          },
        );
      }
    }

    if (telemetryConfig.debug) {
      if (loggerCallback) {
        loggerCallback('OTEL Config', 'Telemetry configuration loaded', {
          timestamp: Date.now(),
          level: 'debug',
          context: {
            enabled: telemetryConfig.enabled,
            serviceName: telemetryConfig.serviceName,
            sampleRate: telemetryConfig.sampleRate,
            debug: telemetryConfig.debug,
          },
        });
      }
    }
  }

  return telemetryConfig;
}

/**
 * Singleton instance of telemetry configuration
 * Loaded once at module initialization
 */
export const telemetryConfig = getTelemetryConfig();
